import {
  CheckCircle2Icon,
  ChevronDownIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  CircleAlertIcon,
  FolderIcon,
  FolderUpIcon,
  Loader2Icon,
  RotateCwIcon,
  XIcon,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { toast } from 'sonner';

import {
  fingerprintOf,
  pruneExpiredPendingUploads,
  usePendingUploads,
  type PendingUpload,
} from '@/shared/api/pendingUploads';
import { FileIcon } from '@/shared/components/files';
import { describeUploadStatus } from '@/shared/components/files/uploadStatus';
import { useAuthStore } from '@/shared/auth/store';
import { useFormatters } from '@/shared/i18n/format';
import { cn } from '@/shared/lib/cn';
import { Button } from '@/shared/ui/button';
import { Progress } from '@/shared/ui/progress';

import { folderPath, panelSearch } from '../lib/items';

import {
  diskResumeMeta,
  uploadSummary,
  useUploadStore,
  type DiskUploadTask,
  type UploadBatch,
} from './uploadStore';

/** An upload a page reload cut off: continues when the same file is chosen again. */
function InterruptedRow({ entry }: { entry: PendingUpload }) {
  const { t } = useTranslation('disk');
  const fmt = useFormatters();
  const enqueue = useUploadStore((s) => s.enqueue);
  const discard = useUploadStore((s) => s.discardInterrupted);
  const input = useRef<HTMLInputElement>(null);
  const meta = diskResumeMeta(entry);
  if (!meta) return null;

  const onPick = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (fingerprintOf(file) !== entry.fingerprint) {
      toast.error(t('upload.notSameFile', { name: file.name }));
      return;
    }
    // The upload finds its session by the file's fingerprint and continues where the server left off.
    enqueue([file], meta.target, () => meta.conflict);
  };

  return (
    <li className="flex items-center gap-3 px-4 py-2.5" data-testid="disk-upload-interrupted">
      <FileIcon name={entry.name} contentType={entry.contentType} size="sm" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{meta.path ?? entry.name}</p>
        <p className="truncate text-xs text-muted-foreground">
          {t('upload.interruptedSent', {
            sent: fmt.fileSize(entry.offset),
            total: fmt.fileSize(entry.size),
            folder: meta.target.folderName,
          })}
        </p>
      </div>
      <input
        ref={input}
        type="file"
        className="hidden"
        onChange={onPick}
        data-testid="disk-upload-resume-input"
      />
      <Button
        variant="outline"
        size="xs"
        onClick={() => input.current?.click()}
        aria-label={t('upload.continueFile', { name: entry.name })}
        data-testid="disk-upload-resume"
      >
        {t('upload.continue')}
      </Button>
      <Button
        variant="ghost"
        size="icon-xs"
        onClick={() => discard(entry)}
        aria-label={t('upload.discardFile', { name: entry.name })}
        data-testid="disk-upload-discard"
      >
        <XIcon />
      </Button>
    </li>
  );
}

function TaskRow({ task }: { task: DiskUploadTask }) {
  const { t } = useTranslation('disk');
  const fmt = useFormatters();
  const cancel = useUploadStore((s) => s.cancel);
  const retry = useUploadStore((s) => s.retry);
  const note = task.connection ? describeUploadStatus(task.connection, t, fmt.fileSize, 'upload') : undefined;
  const running = task.status === 'uploading' || task.status === 'saving' || task.status === 'queued';
  return (
    <li
      className="flex items-center gap-3 px-4 py-2.5"
      data-status={task.status}
      data-testid="disk-upload-task"
    >
      <FileIcon name={task.file.name} contentType={task.file.type} size="sm" />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          {task.status === 'done' && task.itemId ? (
            <Link
              to={{ pathname: folderPath(task.target.folderId), search: panelSearch(task.itemId) }}
              className="min-w-0 truncate text-sm font-medium hover:underline"
            >
              {task.path ?? task.file.name}
            </Link>
          ) : (
            <span className="min-w-0 truncate text-sm font-medium">{task.path ?? task.file.name}</span>
          )}
        </div>
        {task.status === 'error' ? (
          <p className="truncate text-xs text-destructive-text" role="alert">
            {task.error}
          </p>
        ) : (
          <p className="flex min-w-0 items-center gap-1 text-xs text-muted-foreground">
            <span className="shrink-0">
              {task.status === 'queued' && t('upload.queued')}
              {task.status === 'uploading' &&
                `${fmt.fileSize((task.file.size * task.progress) / 100)} / ${fmt.fileSize(task.file.size)}`}
              {task.status === 'saving' && t('upload.saving')}
              {task.status === 'done' && fmt.fileSize(task.file.size)}
            </span>
            {task.status === 'uploading' && note && (
              <>
                <span aria-hidden>·</span>
                <span className="truncate" data-testid="disk-upload-note">
                  {note}
                </span>
              </>
            )}
            {!task.batch && (
              <>
                <span aria-hidden>·</span>
                <FolderIcon className="size-3 shrink-0" aria-hidden />
                <span className="truncate">{task.target.folderName}</span>
              </>
            )}
          </p>
        )}
        {task.status === 'uploading' && (
          <Progress
            value={task.progress}
            className="mt-1.5 h-1"
            aria-label={t('upload.progress', { name: task.file.name })}
          />
        )}
      </div>
      <div className="flex shrink-0 items-center">
        {task.status === 'done' && (
          <CheckCircle2Icon className="size-4 text-success-text" aria-label={t('upload.done')} />
        )}
        {task.status === 'saving' && (
          <Loader2Icon className="size-4 animate-spin text-muted-foreground" aria-hidden />
        )}
        {task.status === 'error' && (
          <Button
            variant="ghost"
            size="icon-xs"
            onClick={() => retry(task.key)}
            aria-label={t('upload.retry')}
          >
            <RotateCwIcon />
          </Button>
        )}
        {(running || task.status === 'error') && task.status !== 'saving' && (
          <Button
            variant="ghost"
            size="icon-xs"
            onClick={() => cancel(task.key)}
            aria-label={t('upload.cancel')}
          >
            <XIcon />
          </Button>
        )}
      </div>
    </li>
  );
}

/** A folder upload as one row: its folders, how many files went through, and the files themselves on demand. */
function BatchRow({ batch, tasks }: { batch: UploadBatch; tasks: DiskUploadTask[] }) {
  const { t } = useTranslation('disk');
  const cancelBatch = useUploadStore((s) => s.cancelBatch);
  const retryFailed = useUploadStore((s) => s.retryFailed);
  const [expanded, setExpanded] = useState(false);
  const summary = uploadSummary(tasks);
  return (
    <li
      data-testid="disk-upload-batch"
      data-status={summary.active > 0 ? 'active' : summary.failed > 0 ? 'failed' : 'done'}
    >
      <div className="flex items-center gap-3 px-4 py-2.5">
        <Button
          variant="ghost"
          size="icon-xs"
          onClick={() => setExpanded(!expanded)}
          aria-expanded={expanded}
          aria-label={expanded ? t('upload.hideFiles') : t('upload.showFiles')}
        >
          {expanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
        </Button>
        <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-brand-soft text-brand-text">
          <FolderUpIcon className="size-4" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">{batch.label}</p>
          <p className="flex min-w-0 items-center gap-1 text-xs text-muted-foreground">
            <span className="shrink-0 tabular-nums" data-testid="disk-upload-batch-count">
              {t('upload.batchFiles', { done: summary.done, total: summary.total })}
            </span>
            {summary.failed > 0 && (
              <>
                <span aria-hidden>·</span>
                <span className="shrink-0 text-destructive-text" role="alert">
                  {t('upload.batchFailed', { count: summary.failed })}
                </span>
              </>
            )}
            <span aria-hidden>·</span>
            <FolderIcon className="size-3 shrink-0" aria-hidden />
            <span className="truncate">{batch.folderName}</span>
          </p>
          {summary.active > 0 && (
            <Progress
              value={summary.percent}
              className="mt-1.5 h-1"
              aria-label={t('upload.progress', { name: batch.label })}
            />
          )}
        </div>
        <div className="flex shrink-0 items-center">
          {summary.active === 0 && summary.failed === 0 && (
            <CheckCircle2Icon className="size-4 text-success-text" aria-label={t('upload.done')} />
          )}
          {summary.failed > 0 && (
            <Button
              variant="ghost"
              size="icon-xs"
              onClick={() => retryFailed(batch.id)}
              aria-label={t('upload.retryFailed')}
              data-testid="disk-upload-retry-failed"
            >
              <RotateCwIcon />
            </Button>
          )}
          {summary.active > 0 && (
            <Button
              variant="ghost"
              size="icon-xs"
              onClick={() => cancelBatch(batch.id)}
              aria-label={t('upload.cancelFolder')}
            >
              <XIcon />
            </Button>
          )}
        </div>
      </div>
      {expanded && (
        <ul className="divide-y border-t bg-muted/30 pl-6">
          {tasks.map((task) => (
            <TaskRow key={task.key} task={task} />
          ))}
        </ul>
      )}
    </li>
  );
}

type Row = { batch: UploadBatch; tasks: DiskUploadTask[] } | { batch?: undefined; task: DiskUploadTask };

/** The panel's rows in the order the work arrived: single files, and one row per folder upload. */
function rows(tasks: readonly DiskUploadTask[], batches: readonly UploadBatch[]): Row[] {
  const result: Row[] = [];
  const seen = new Set<string>();
  for (const task of tasks) {
    if (!task.batch) {
      result.push({ task });
    } else if (!seen.has(task.batch)) {
      seen.add(task.batch);
      const batch = batches.find((b) => b.id === task.batch);
      if (batch) result.push({ batch, tasks: tasks.filter((t) => t.batch === batch.id) });
    }
  }
  return result;
}

/** Floating card with the disk's uploads (bottom right, above pages and side panels, below dialogs). */
export function UploadPanel() {
  const { t } = useTranslation('disk');
  const tasks = useUploadStore((s) => s.tasks);
  const batches = useUploadStore((s) => s.batches);
  const open = useUploadStore((s) => s.open);
  const collapsed = useUploadStore((s) => s.collapsed);
  const setCollapsed = useUploadStore((s) => s.setCollapsed);
  const dismiss = useUploadStore((s) => s.dismiss);
  const setOpen = useUploadStore((s) => s.setOpen);
  const interruptedHidden = useUploadStore((s) => s.interruptedHidden);
  const userId = useAuthStore((s) => s.user?.id);
  const entries = usePendingUploads((s) => s.entries);
  const refreshPending = usePendingUploads((s) => s.refresh);
  // Uploads a page reload cut off are read from this browser's storage once the user is known, and kept in step with it.
  useEffect(() => {
    pruneExpiredPendingUploads();
    refreshPending();
  }, [userId, refreshPending]);
  const interrupted = useMemo(
    () =>
      entries.filter(
        (entry) =>
          diskResumeMeta(entry) !== null &&
          !tasks.some((task) => fingerprintOf(task.file) === entry.fingerprint),
      ),
    [entries, tasks],
  );
  const showsInterrupted = interrupted.length > 0 && !interruptedHidden;
  if (tasks.length > 0 ? !open : !showsInterrupted) return null;
  const summary = uploadSummary(tasks);
  const title =
    tasks.length === 0
      ? t('upload.interrupted')
      : summary.active > 0
        ? t('upload.uploading', { count: summary.active })
        : summary.failed > 0
          ? t('upload.failed', { count: summary.failed })
          : t('upload.finished', { count: summary.done });

  return createPortal(
    <section
      aria-label={t('upload.panel')}
      data-testid="disk-upload-panel"
      // Usable while a side panel is open: a modal panel disables pointer events outside it and closes on outside
      // clicks, so the card takes pointer events back and keeps its clicks to itself.
      onPointerDown={(e) => e.stopPropagation()}
      className="pointer-events-auto fixed right-4 bottom-4 z-[60] w-[min(24rem,calc(100vw-2rem))] overflow-hidden rounded-xl border bg-popover text-popover-foreground shadow-xl"
    >
      <header className="flex items-center gap-2 border-b bg-surface/60 py-2 pr-2 pl-4">
        {summary.active > 0 ? (
          <Loader2Icon className="size-4 shrink-0 animate-spin text-brand-text" aria-hidden />
        ) : summary.failed > 0 || tasks.length === 0 ? (
          <CircleAlertIcon className="size-4 shrink-0 text-destructive-text" aria-hidden />
        ) : (
          <CheckCircle2Icon className="size-4 shrink-0 text-success-text" aria-hidden />
        )}
        <h2 className="min-w-0 flex-1 truncate text-sm font-semibold" aria-live="polite">
          {title}
        </h2>
        {summary.active > 0 && (
          <span className="text-xs text-muted-foreground tabular-nums">{summary.percent}%</span>
        )}
        <Button
          variant="ghost"
          size="icon-xs"
          onClick={() => setCollapsed(!collapsed)}
          aria-label={collapsed ? t('upload.expand') : t('upload.collapse')}
          aria-expanded={!collapsed}
        >
          {collapsed ? <ChevronUpIcon /> : <ChevronDownIcon />}
        </Button>
        <Button
          variant="ghost"
          size="icon-xs"
          // While files are still uploading the panel only hides (the top bar shows the progress).
          onClick={summary.active > 0 ? () => setOpen(false) : dismiss}
          aria-label={summary.active > 0 ? t('upload.hide') : t('upload.close')}
          data-testid="disk-upload-close"
        >
          <XIcon />
        </Button>
      </header>
      {summary.active > 0 && <Progress value={summary.percent} className="h-0.5 rounded-none" aria-hidden />}
      <ul className={cn('max-h-72 divide-y overflow-y-auto', collapsed && 'hidden')}>
        {rows(tasks, batches).map((row) =>
          row.batch ? (
            <BatchRow key={row.batch.id} batch={row.batch} tasks={row.tasks} />
          ) : (
            <TaskRow key={row.task.key} task={row.task} />
          ),
        )}
        {showsInterrupted && (
          <li>
            {tasks.length > 0 && (
              <p className="bg-muted/30 px-4 py-1.5 text-xs font-medium text-muted-foreground">
                {t('upload.interrupted')}
              </p>
            )}
            <p className="px-4 pt-2 text-xs text-muted-foreground">{t('upload.interruptedHint')}</p>
            <ul className="divide-y">
              {interrupted.map((entry) => (
                <InterruptedRow key={entry.fingerprint} entry={entry} />
              ))}
            </ul>
          </li>
        )}
      </ul>
    </section>,
    document.body,
  );
}
